import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import posthog from 'posthog-js';
import BreakdownRow from '../components/BreakdownRow';
import BottomTabBar, { TAB_BAR_HEIGHT } from '../components/BottomTabBar';
import { formatDeadlineBadge, dueDateToQuarter, quarterDueDate } from '../utils/dateHelpers';
import { taxReservePlan, slowSeasonPlan, curveToMonthlyDollars, monthIndexToLabel } from '../utils/planHelpers';
import { unmarkQuarterPaid } from '../services/quarterlyPayments';
import { createSavingsEntry, deleteSavingsEntry } from '../services/savings';
import { INSIGHT_TYPES } from '../constants/insightTypes';
import useTruflowData from '../hooks/useTruflowData';

const BLUE       = 'var(--signal)';
const BLUE_LIGHT = 'var(--signal-soft)';
const GREEN      = 'var(--go)';
const RED        = 'var(--stop)';
const RED_BG     = 'var(--stop-soft)';
const TEXT1      = 'var(--ink)';
const TEXT2      = 'var(--slate)';
const TEXT3      = 'var(--mute)';
const SURFACE    = 'var(--paper)';
// Chart-only tones with no token equivalent — see migration notes.
const BAR_LOW    = 'var(--bar-low)';
const BAR_MID    = 'var(--bar-mid)';
const MONTH_INITIALS = ['J','F','M','A','M','J','J','A','S','O','N','D'];

// IRS quarterly period starts, as month indexes — drives elapsed-fraction
// pacing. Deadlines come from quarterDueDate in dateHelpers, which applies the
// same weekend adjustment the backend does.
const QUARTER_START_MONTH = { 1: 0, 2: 3, 3: 5, 4: 8 };   // Jan, Apr, Jun, Sep

function fmt(n) {
  return '$' + Math.round(n).toLocaleString();
}

// Local YYYY-MM-DD for the transfer-date input default (avoids UTC drift).
function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function formatEntryDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}


export const CARD_GAP = 14;

function card(extra) {
  return {
    backgroundColor: 'var(--card)',
    boxShadow: 'var(--sh-card)',
    borderRadius: 'var(--r-card)',
    padding: 16,
    marginBottom: CARD_GAP,
    ...extra,
  };
}

function StickyHeader({ navigate }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px',
    }}>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function Greeting({ firstName }) {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <p style={{ color: TEXT2, fontSize: 13, margin: '18px 0 12px' }}>
      Hey {firstName} · {weekday}, {monthDay}
    </p>
  );
}

function PrimaryButton({ label, onClick, style }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '12px', backgroundColor: BLUE,
      color: 'var(--on-signal)', fontSize: '0.95rem', fontWeight: 600,
      border: 'none', borderRadius: 10, cursor: 'pointer',
      ...style,
    }}>
      {label}
    </button>
  );
}

function UndoConfirmModal({ quarter, taxYear, markedPaidAt, onCancel, onConfirm, loading, error }) {
  const formattedDate = new Date(markedPaidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 320, width: '90%', backgroundColor: 'var(--card)', borderRadius: 'var(--r-card)', padding: 20 }}
      >
        <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: TEXT1 }}>
          Undo Q{quarter} {taxYear}?
        </p>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          You marked this paid on {formattedDate}. Your dashboard will reset to show this quarter as due again.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1, padding: '10px 16px',
              backgroundColor: 'var(--paper)', color: TEXT2,
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 500,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1, padding: '10px 16px',
              backgroundColor: RED, color: 'var(--on-stop)',
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 500,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Undoing...' : 'Undo payment'}
          </button>
        </div>
        {error && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: RED }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function PaidQuartersStrip({ taxYear, paidQuarters, nextDueQuarter, nextDueDate, onUndoSuccess }) {
  const [selectedPaidQuarter, setSelectedPaidQuarter] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoError, setUndoError] = useState(null);
  const [hoveredQuarter, setHoveredQuarter] = useState(null);

  const paidMap = new Map(paidQuarters.map((p) => [p.quarter, p]));
  // Which visual states actually appear this year — the legend only earns its
  // line when there are more than two.
  const statesShown = new Set();

  const todayMid = new Date();
  todayMid.setHours(0, 0, 0, 0);
  function isPastDeadline(q) {
    const due = quarterDueDate(taxYear, q);
    return due !== null && due < todayMid;
  }

  function formatShortDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  async function handleConfirmUndo() {
    setUndoLoading(true);
    setUndoError(null);
    try {
      await unmarkQuarterPaid(taxYear, selectedPaidQuarter.quarter);
      await onUndoSuccess();
      setSelectedPaidQuarter(null);
    } catch {
      setUndoError("Couldn't undo. Please try again.");
    }
    setUndoLoading(false);
  }

  function handleCancelUndo() {
    setSelectedPaidQuarter(null);
    setUndoError(null);
  }

  return (
    <>
      {selectedPaidQuarter && (
        <UndoConfirmModal
          quarter={selectedPaidQuarter.quarter}
          taxYear={taxYear}
          markedPaidAt={selectedPaidQuarter.markedPaidAt}
          onCancel={handleCancelUndo}
          onConfirm={handleConfirmUndo}
          loading={undoLoading}
          error={undoError}
        />
      )}
      <div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {[1, 2, 3, 4].map((q) => {
            const record = paidMap.get(q);
            const paid = !!record;
            const dueSoon = !paid && q === nextDueQuarter;
            const pastUnmarked = !paid && !dueSoon && isPastDeadline(q);
            if (paid) statesShown.add('paid');
            else if (dueSoon) statesShown.add('current');
            else if (pastUnmarked) statesShown.add('past');
            else statesShown.add('upcoming');

            if (paid) {
              return (
                <div
                  key={q}
                  onClick={() => setSelectedPaidQuarter({ quarter: q, markedPaidAt: record.marked_paid_at })}
                  onMouseEnter={() => setHoveredQuarter(q)}
                  onMouseLeave={() => setHoveredQuarter(null)}
                  style={{
                    flex: 1, textAlign: 'center',
                    padding: '6px 8px', borderRadius: 8,
                    cursor: 'pointer',
                    backgroundColor: hoveredQuarter === q ? 'rgba(0,0,0,0.03)' : 'transparent',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: GREEN }}>
                    Q{q} ✓
                  </span>
                </div>
              );
            }

            if (dueSoon) {
              return (
                <div key={q} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: BLUE, lineHeight: 1.3 }}>Q{q}</div>
                  <div style={{ fontSize: 10, color: BLUE }}>{formatShortDate(nextDueDate)}</div>
                </div>
              );
            }

            // Past deadline and not marked. Absent from /payments/quarterly means
            // unmarked, not unpaid — so it stays muted, never red. The affordance
            // is disabled: see the note below the strip.
            if (pastUnmarked) {
              return (
                <div key={q} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: TEXT3, lineHeight: 1.3 }}>Q{q}</div>
                  <span
                    aria-disabled="true"
                    title="Unavailable — see note below"
                    style={{ fontSize: 10, color: TEXT3, opacity: 0.55, cursor: 'not-allowed' }}
                  >
                    Mark paid
                  </span>
                </div>
              );
            }

            return (
              <div key={q} style={{ flex: 1, textAlign: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: TEXT3 }}>
                  Q{q}
                </span>
              </div>
            );
          })}
        </div>
        {statesShown.has('past') && (
          <p style={{ margin: '8px 0 0', fontSize: 10.5, color: 'var(--mute)', lineHeight: 1.5 }}>
            Marking an earlier quarter paid would debit this quarter's set-aside,
            so it's unavailable here for now.
          </p>
        )}
        {statesShown.size > 2 && (
          <p style={{ margin: '8px 0 0', fontSize: 10.5, color: 'var(--mute)' }}>
            ✓ paid · {nextDueQuarter ? `Q${nextDueQuarter} due next` : 'due next'} · remaining quarters ahead
          </p>
        )}
      </div>
    </>
  );
}

function OverviewCard({ taxData, taxSetupData, navigate }) {
  const currentYear = new Date().getFullYear();
  const isComplete = taxData !== null && taxSetupData !== null && taxSetupData.annual_income != null;

  if (!isComplete) {
    return (
      <div style={card()}>
        <p style={{ margin: '0 0 8px', fontSize: 11, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {currentYear} Overview
        </p>
        <p style={{ margin: '0 0 14px', fontSize: 14, color: TEXT2, lineHeight: 1.5 }}>
          Finish setting up to see your annual take-home, tax burden, and income overview.
        </p>
        <PrimaryButton label="Finish setup" onClick={() => navigate('/onboarding/welcome')} />
      </div>
    );
  }

  const annual_income = taxSetupData.annual_income;
  const annual_tax    = taxData.total_annual_tax;
  const take_home     = annual_income - annual_tax;

  return (
    <div style={card()}>
      <p style={{ margin: 0, fontSize: 11, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {currentYear} Overview
      </p>
      <p style={{ margin: '4px 0 2px', fontFamily: 'var(--font-serif)', fontSize: 40, fontWeight: 700, color: TEXT1, lineHeight: 1.1 }}>
        {fmt(take_home)}
      </p>
      <p style={{ margin: '0 0 14px', fontSize: 14, color: TEXT2 }}>
        take-home
      </p>
      <p style={{ margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
        After {fmt(annual_tax)} in taxes · {fmt(annual_income)} estimated income
      </p>
    </div>
  );
}

// Shared by the insight card and the 90-day card: a tight month ahead promotes
// a "hold back" line, which must render exactly once — under the insight
// headline when an insight is showing, otherwise beneath the month rows.
function holdBackState(cashflowData, negativeMonthInsight) {
  const months = (cashflowData?.months ?? []).slice(0, 3);
  const tight = months.some((m) => (m.net_cash_flow ?? m.net ?? 0) < 0);
  return {
    months,
    showActionLine: tight,
    holdBackText: negativeMonthInsight?.body ?? 'A month ahead looks tight — tap to see the details.',
  };
}

// Emphasizes the dollar figure within the hold-back sentence; leaves the rest
// of the sentence at normal weight.
function renderHoldBack(text) {
  const match = text.match(/\$[\d,]+(?:\.\d+)?/);
  if (!match) return text;
  const amount = match[0];
  const start = match.index;
  return (
    <>
      {text.slice(0, start)}
      <span style={{ fontWeight: 600 }}>{amount}</span>
      {text.slice(start + amount.length)}
    </>
  );
}

// Lead card on Home: the insight carousel, styled as the screen's primary
// action. Renders nothing when no insight qualifies, so its slot collapses
// rather than leaving an empty card behind.
function InsightCard({ insights, cashflowData, negativeMonthInsight, navigate, onAddExpense, onStartBanking }) {
  const qualifying = insights ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const viewedTypesRef = useRef(new Set());

  const insight = qualifying[activeIndex] ?? null;
  const multi = qualifying.length > 1;

  const { showActionLine, holdBackText } = holdBackState(cashflowData, negativeMonthInsight);
  const hideSlideBody = showActionLine && negativeMonthInsight != null
    && insight?.type === INSIGHT_TYPES.NEGATIVE_MONTH_MOVE;

  useEffect(() => {
    if (insight && !viewedTypesRef.current.has(insight.type)) {
      viewedTypesRef.current.add(insight.type);
      posthog.capture('insight_viewed', { type: insight.type, placement: 'card' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insight?.type]);

  if (!insight) return null;

  // No severity field on the payload — type is the only signal for whether an
  // insight is reporting a problem rather than progress.
  const urgent = insight.type === INSIGHT_TYPES.NEGATIVE_MONTH_MOVE
    || insight.type === INSIGHT_TYPES.DEADLINE_PACE;
  const accent = urgent ? 'var(--stop)' : 'var(--signal)';

  function advance() {
    const nextIndex = (activeIndex + 1) % qualifying.length;
    posthog.capture('insight_cycled', { from_type: qualifying[activeIndex].type, to_type: qualifying[nextIndex].type });
    setActiveIndex(nextIndex);
  }

  function handleCtaClick() {
    posthog.capture('insight_cta_clicked', { type: insight.type });
    if (insight.type === INSIGHT_TYPES.DEADLINE_PACE) navigate('/irs-payment');
    if (insight.type === INSIGHT_TYPES.DEDUCTION_RUNNING_TOTAL) onAddExpense?.();
    if (insight.type === INSIGHT_TYPES.SLOW_SEASON_RUNWAY) onStartBanking?.();
    if (insight.type === INSIGHT_TYPES.NEGATIVE_MONTH_MOVE) navigate('/cashflow');
  }

  return (
    <div style={{
      backgroundColor: 'var(--card)',
      boxShadow: 'var(--sh-raise)',
      borderRadius: 'var(--r-card)',
      borderLeft: `3px solid ${accent}`,
      padding: '20px 16px',
      marginBottom: CARD_GAP,
    }}>
      <p style={{
        margin: '0 0 8px', fontSize: 11, fontWeight: 600,
        letterSpacing: '.08em', textTransform: 'uppercase', color: accent,
      }}>
        Heads Up
      </p>
      <div>
        <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
          {insight.headline}
        </p>
        {!hideSlideBody && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
            {insight.body}
          </p>
        )}
        {showActionLine && (
          <p style={{ margin: '6px 0 0', fontSize: 14, color: TEXT1, lineHeight: 1.5 }}>
            {renderHoldBack(holdBackText)}
          </p>
        )}
        {insight.cta && (
          <button onClick={handleCtaClick} style={{
            marginTop: 12, width: '100%',
            backgroundColor: 'var(--signal)', color: 'var(--on-signal)',
            border: 'none', borderRadius: 14, padding: 15,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            {insight.cta}
          </button>
        )}
      </div>
      {multi && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12,
        }}>
          <button
            onClick={advance}
            aria-label="Next insight"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 8px 8px 0', minHeight: 32,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {qualifying.map((_, i) => (
              <span key={i} style={{
                display: 'inline-block',
                width: i === activeIndex ? 16 : 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: i === activeIndex ? BLUE : TEXT3,
                transition: 'width 0.2s, background-color 0.2s',
              }} />
            ))}
          </button>
          <button
            onClick={advance}
            aria-label="Next insight"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 8, minHeight: 32, minWidth: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ stroke: TEXT3 }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="5 2 10 7 5 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// The 90-day month rows. The "Next 90 days" heading now lives outside the card
// as a section eyebrow, so this holds only the rows. Taps through to /cashflow.
function CashflowSummaryCard({ insights, cashflowData, negativeMonthInsight, navigate }) {
  const hasInsight = (insights ?? []).length > 0;
  const { months, showActionLine, holdBackText } = holdBackState(cashflowData, negativeMonthInsight);

  return (
    <div style={card()}>
      <div
        onClick={() => navigate('/cashflow')}
        role="button"
        tabIndex={0}
        aria-label="View 90-day cash flow"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/cashflow'); } }}
        style={{ cursor: 'pointer' }}
      >
        {months.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
            Complete your income and tax setup to see your projection.
          </p>
        ) : (
          months.map((m, i) => {
            const net = m.net_cash_flow ?? m.net ?? 0;
            const taxDue = !!m.is_tax_month;
            return (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '7px 0',
                borderBottom: i < months.length - 1 ? '0.5px solid rgba(0,0,0,0.06)' : 'none',
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: TEXT1 }}>
                  {m.month_name}
                  {taxDue && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: RED, backgroundColor: RED_BG, padding: '2px 8px', borderRadius: 20 }}>
                      Tax due
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: net >= 0 ? GREEN : RED }}>
                  {net >= 0 ? '+' : '−'}{fmt(Math.abs(net))}
                </span>
              </div>
            );
          })
        )}
      </div>
      {!hasInsight && showActionLine && (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: TEXT1, lineHeight: 1.5 }}>
          {renderHoldBack(holdBackText)}
        </p>
      )}
    </div>
  );
}

// Small uppercase label above a card group, sitting on the page surface rather
// than inside the card it introduces.
export function SectionEyebrow({ children }) {
  return (
    <p style={{
      margin: '20px 0 9px', fontSize: 10.5, fontWeight: 700,
      letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--mute)',
    }}>
      {children}
    </p>
  );
}

// Verdict copy for the reserve-target tradeoff. Describes what each figure means
// at filing time; it never recommends an amount — TruFlow is a coaching tool,
// not a tax advisor.
//
// These figures are TruFlow's recommended cushion, NOT the IRS safe harbor —
// the backend takes the larger of the two inputs where §6654 takes the smaller
// (see routers/tax.py::_compute_tax_reserve). Copy here must not promise
// penalty protection or imply any legal threshold has been met.
function ReserveTargetVerdict({ currentYearQuarterly, priorYearBenchmarkQuarterly, navigate }) {
  const box = {
    backgroundColor: 'var(--sunk)', borderRadius: 12, padding: 13,
    marginTop: 12, fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.55,
  };

  if (priorYearBenchmarkQuarterly == null) {
    return (
      <p style={{ ...box, margin: '12px 0 0' }}>
        <button
          onClick={() => navigate('/settings/tax')}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            font: 'inherit', color: 'var(--signal)', fontWeight: 600, textAlign: 'left',
          }}
        >
          Add last year's total tax in settings
        </button>
        {' '}and we'll factor it into your reserve target.
      </p>
    );
  }

  if (currentYearQuarterly == null) return null;

  // Both figures are an annual total divided by 4 on the backend, so the gap
  // times four is the projected balance at filing.
  if (priorYearBenchmarkQuarterly < currentYearQuarterly) {
    const stillOwed = Math.round((currentYearQuarterly - priorYearBenchmarkQuarterly) * 4);
    return (
      <p style={{ ...box, margin: '12px 0 0' }}>
        Your target is based on this year's projected income. Reserving only last
        year's amount would leave about {fmt(stillOwed)} to pay when you file in April.
      </p>
    );
  }

  return (
    <p style={{ ...box, margin: '12px 0 0' }}>
      Your target is based on last year's tax, which runs above this year's
      projection — a deliberate cushion if you end up earning more than expected.
    </p>
  );
}

export function TaxReserveCard({ taxData, savingsData, navigate }) {
  if (!taxData) {
    return (
      <div style={card()}>
        <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: TEXT1 }}>Quarterly tax reserve</p>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: TEXT2 }}>Complete your tax setup to see your reserve.</p>
        <PrimaryButton label="Set up taxes" onClick={() => navigate('/onboarding/tax')} />
      </div>
    );
  }

  const { recommended_reserve, next_due_date, due_date } = taxData;
  const resolvedDueDate = next_due_date ?? due_date;
  const deadlineBadge = formatDeadlineBadge(resolvedDueDate);
  const badgeAlert = deadlineBadge?.urgency === 'critical' || deadlineBadge?.urgency === 'overdue';

  // Verdict chip. Same target source and behind rule the Home hero uses, off
  // the savings slice already in the shared hook — no request added.
  const target = buildTaxPlan(taxData)?.target ?? null;
  const balance = savingsData?.tax?.balance ?? 0;
  const hasTarget = target != null && target > 0;
  const pct = hasTarget ? Math.min(balance / target, 1) : 0;
  const dueAt = resolvedDueDate ? new Date(resolvedDueDate + 'T00:00:00') : null;
  const todayAt = new Date();
  todayAt.setHours(0, 0, 0, 0);
  const daysToDue = dueAt && !isNaN(dueAt.getTime())
    ? Math.round((dueAt - todayAt) / 86400000)
    : null;
  const behind = hasTarget && pct < 0.25 && daysToDue !== null && daysToDue < 30;

  // Pace. IRS periods are fixed, so the period start follows from which quarter
  // the due date belongs to (Q1 Jan 1, Q2 Apr 1, Q3 Jun 1, Q4 Sep 1). If the due
  // date doesn't map to a quarter, paceTarget stays null and On track falls back
  // to fully funded rather than guessing at a start.
  const paceTarget = hasTarget ? quarterPaceTarget(resolvedDueDate, target, todayAt) : null;
  const onTrack = hasTarget
    && (balance >= target || (paceTarget !== null && balance >= paceTarget));

  // Only the two states the badge below doesn't already say. A neutral
  // "Due in N days" chip would just repeat the deadline badge.
  let chip = null;
  if (behind) {
    chip = { tone: 'stop', label: 'Behind' };
  } else if (onTrack) {
    chip = { tone: 'go', label: 'On track' };
  }

  return (
    <div style={card()}>
      <p style={{ margin: '0 0 2px', fontSize: 11, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quarterly tax reserve</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 0 2px' }}>
        <p style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 40, fontWeight: 700, color: TEXT1, lineHeight: 1.1 }}>
          {fmt(recommended_reserve)}
        </p>
        {chip && (
          <PlanBadge tone={chip.tone} dot style={{ marginTop: 0 }}>
            {chip.label}
          </PlanBadge>
        )}
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: TEXT2 }}>Recommended this quarter</p>
      {deadlineBadge && (
        <span style={{
          display: 'inline-block', marginBottom: 16,
          backgroundColor: badgeAlert ? RED_BG : BLUE_LIGHT,
          color: badgeAlert ? RED : BLUE,
          fontSize: 12, fontWeight: 600, padding: '3px 10px',
          borderRadius: 20,
        }}>
          {deadlineBadge.label}
        </span>
      )}
      <PrimaryButton
        label="Pay now — IRS Direct Pay"
        onClick={() => { posthog.capture('irs_payment_guide_opened'); navigate('/irs-payment'); }}
      />
    </div>
  );
}

// Quarter strip in its own card. Returns null when there is nothing to show, so
// the section eyebrow above it can be suppressed on the same condition.
export function quarterProgressAvailable(taxData, paidQuartersData) {
  if (!taxData || paidQuartersData === null) return null;
  return dueDateToQuarter(taxData.next_due_date ?? taxData.due_date);
}

export function QuarterProgressCard({ taxData, paidQuartersData, onPaidQuartersChanged }) {
  const quarterInfo = quarterProgressAvailable(taxData, paidQuartersData);
  if (!quarterInfo) return null;
  const resolvedDueDate = taxData.next_due_date ?? taxData.due_date;

  return (
    <div style={card()}>
      <PaidQuartersStrip
        taxYear={quarterInfo.tax_year}
        paidQuarters={paidQuartersData.filter((p) => p.tax_year === quarterInfo.tax_year)}
        nextDueQuarter={quarterInfo.quarter}
        nextDueDate={resolvedDueDate}
        onUndoSuccess={onPaidQuartersChanged}
      />
    </div>
  );
}

export function AnnualBreakdownCard({ taxData, navigate }) {
  if (!taxData) return null;
  const {
    se_tax, federal_income_tax, state_income_tax,
    effective_rate, prior_year_benchmark_quarterly, current_year_quarterly,
  } = taxData;

  return (
    <div style={card()}>
      {se_tax != null && <BreakdownRow label="Self-employment tax" value={fmt(se_tax)} />}
      {federal_income_tax != null && <BreakdownRow label="Federal income tax" value={fmt(federal_income_tax)} />}
      {state_income_tax != null && <BreakdownRow label="State income tax" value={fmt(state_income_tax)} />}
      {effective_rate != null && <BreakdownRow label="Effective rate" value={`${(effective_rate * 100).toFixed(1)}%`} />}

      <p style={{ margin: '14px 0 6px', fontSize: 11, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quarterly payment</p>
      {current_year_quarterly != null && <BreakdownRow label="Current year quarterly" value={fmt(current_year_quarterly)} />}
      {prior_year_benchmark_quarterly != null && (
        <BreakdownRow
          label="Based on last year's tax"
          value={fmt(prior_year_benchmark_quarterly)}
        />
      )}
      <ReserveTargetVerdict
        currentYearQuarterly={current_year_quarterly}
        priorYearBenchmarkQuarterly={prior_year_benchmark_quarterly}
        navigate={navigate}
      />
    </div>
  );
}

export function IncomePatternCard({ curveData, navigate }) {
  if (!curveData) {
    return (
      <div style={card()}>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: TEXT2 }}>Set your income curve to improve projections.</p>
        <PrimaryButton label="Set income pattern" onClick={() => navigate('/onboarding/curve')} />
      </div>
    );
  }

  const weights = curveData.month_weights ?? curveData.monthly_weights ?? [];
  const max = Math.max(...weights, 1);

  function barColor(w) {
    if (w >= 12) return BLUE;
    if (w <= 3)  return BAR_LOW;
    return BAR_MID;
  }

  return (
    <div style={card()}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 14 }}>
        <button onClick={() => navigate('/settings/curve')} style={{ background: 'none', border: 'none', color: BLUE, fontSize: 13, cursor: 'pointer', padding: 0 }}>
          Edit pattern
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 52 }}>
        {weights.map((w, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{
              width: '100%',
              height: `${Math.max(4, (w / max) * 44)}px`,
              backgroundColor: barColor(w),
              borderRadius: '3px 3px 0 0',
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
        {MONTH_INITIALS.map((m, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: TEXT3 }}>{m}</div>
        ))}
      </div>
    </div>
  );
}

// Which IRS quarter a projected tax month belongs to, keyed by the first three
// letters of month_name so it works whether the backend sends "Sep" or
// "September". Q4's deadline falls in January of the following year.
const TAX_MONTH_QUARTER = { Apr: 1, Jun: 2, Sep: 3, Jan: 4 };

// "Tax due September 15" from a projected month, using the same weekend-adjusted
// deadline the rest of the app uses. Falls back to the generic label when the
// month doesn't map to a quarter.
function taxDueLabel(m) {
  const quarter = TAX_MONTH_QUARTER[String(m.month_name ?? '').slice(0, 3)];
  if (!quarter || m.year == null) return 'Tax reserve';
  const due = quarterDueDate(quarter === 4 ? m.year - 1 : m.year, quarter);
  if (!due) return 'Tax reserve';
  return `Tax due ${due.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
}

// Fallback only: every branch of the backend's negative_month_move copy leads
// with _fmt(shortfall), so the first money match in the body is that figure.
// Used when the corresponding numeric field is absent from insight.data.
function moneyFromBody(insight) {
  const match = insight?.body?.match(/\$[\d,]+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = parseFloat(match[0].replace(/[$,]/g, ''));
  return !isNaN(parsed) && parsed > 0 ? parsed : null;
}

export function CashflowCard({ cashflowData, negativeMonthInsight, navigate }) {
  const months = cashflowData?.months ?? [];
  const message = cashflowData?.message ?? '';
  const hasNegMonth = negativeMonthInsight != null && months.some(m => (m.net_cash_flow ?? m.net ?? 0) < 0);

  useEffect(() => {
    if (hasNegMonth) {
      posthog.capture('insight_viewed', { type: negativeMonthInsight.type, placement: 'cashflow_inline' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (months.length === 0) {
    return (
      <div style={card()}>
        <div style={{
          background: 'var(--paper)',
          borderRadius: 10,
          padding: '16px',
          textAlign: 'center',
          fontSize: 13,
          color: 'var(--mute)',
          lineHeight: 1.6
        }}>
          {message || 'Complete your income and tax setup to see your 90-day projection.'}
        </div>
      </div>
    );
  }

  const nets = months.map((m) => m.net_cash_flow ?? m.net ?? 0);
  const avgNet = nets.reduce((a, b) => a + b, 0) / nets.length;
  const totalNet = nets.reduce((a, b) => a + b, 0);

  let lowestIdx = 0;
  let highestIdx = 0;
  nets.forEach((n, i) => {
    if (n < nets[lowestIdx]) lowestIdx = i;
    if (n > nets[highestIdx]) highestIdx = i;
  });
  const negIdx = nets.findIndex((n) => n < 0);
  const negMonth = negIdx >= 0 ? months[negIdx] : null;
  const taxMonth = months.find((m) => m.is_tax_month) ?? null;

  // Each figure resolves independently: the numeric field when present and
  // correctly typed, otherwise the prose/derived fallback. Per-field, not
  // per-object — a partially populated data object degrades one figure at a
  // time rather than blanking the card.
  const insightData = negativeMonthInsight?.data;
  const bodyMoney = negMonth ? moneyFromBody(negativeMonthInsight) : null;

  // hold_back of 0 is a real value meaning there is no surplus to draw from, so
  // it resolves to null rather than falling through to the regex.
  const holdBack = !negMonth
    ? null
    : typeof insightData?.hold_back === 'number'
      ? (insightData.hold_back > 0 ? insightData.hold_back : null)
      : (bodyMoney ?? Math.abs(nets[negIdx]));

  const shortfall = !negMonth
    ? null
    : (typeof insightData?.shortfall === 'number' && insightData.shortfall > 0)
      ? insightData.shortfall
      : (bodyMoney ?? Math.abs(nets[negIdx]));

  const sourceMonth = negIdx >= 0 && highestIdx !== negIdx ? months[highestIdx] : null;
  const sourceMonthName =
    typeof insightData?.source_month === 'string' && insightData.source_month
      ? insightData.source_month
      : (sourceMonth ? sourceMonth.month_name : null);
  const negativeMonthName =
    typeof insightData?.negative_month === 'string' && insightData.negative_month
      ? insightData.negative_month
      : (negMonth ? negMonth.month_name : null);

  // Nothing to move when every month is under water. The action is suppressed
  // rather than asking the user to move money from nowhere.
  const hasSurplus =
    (typeof insightData?.source_month === 'string' && insightData.source_month.length > 0)
    || nets.some((n) => n > 0);

  // Months of runway before the shortfall lands. At index 0 there are none, so
  // the "or trim" alternative has no denominator and is omitted.
  const trimPerMonth = negIdx > 0 && shortfall != null ? Math.round(shortfall / negIdx) : null;

  const monthCard = (isNeg) => ({
    backgroundColor: 'var(--card)',
    boxShadow: isNeg ? 'var(--sh-raise)' : 'var(--sh-card)',
    borderRadius: 'var(--r-card)',
    padding: 17,
    marginBottom: 12,
  });

  const statCard = {
    backgroundColor: 'var(--card)',
    boxShadow: 'var(--sh-card)',
    borderRadius: 'var(--r-card)',
    padding: 14,
  };
  const statLabel = {
    margin: 0, fontSize: 10.5, fontWeight: 700,
    letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--mute)',
  };
  const statValue = { margin: '6px 0 0', fontSize: 21, fontWeight: 600, letterSpacing: '-.03em' };
  const statNote = { margin: '4px 0 0', fontSize: 11.5, color: 'var(--slate)' };
  const rowLabel = { fontSize: 13, color: TEXT2 };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div style={statCard}>
          <p style={statLabel}>90-day net</p>
          <p style={{ ...statValue, color: totalNet >= 0 ? GREEN : RED }}>
            {totalNet >= 0 ? '+' : '−'}{fmt(Math.abs(totalNet))}
          </p>
          {taxMonth && (
            <p style={statNote}>After the {taxMonth.month_name} payment</p>
          )}
        </div>
        <div style={statCard}>
          <p style={statLabel}>Tightest</p>
          <p style={{ ...statValue, color: nets[lowestIdx] < 0 ? RED : TEXT1 }}>
            {months[lowestIdx].month_name}
          </p>
          <p style={statNote}>
            {nets[lowestIdx] < 0
              ? `Short ${fmt(Math.abs(nets[lowestIdx]))}`
              : `Net +${fmt(nets[lowestIdx])}`}
          </p>
        </div>
      </div>

      {months.map((m, i) => {
        const taxDue = !!m.is_tax_month;
        const net = nets[i];
        const isNeg = net < 0;
        const isSource = sourceMonth != null && i === highestIdx;

        return (
          <div key={i} style={monthCard(isNeg)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: TEXT1 }}>{m.month_name}</span>
              <PlanBadge
                tone={net < 0 ? 'stop' : net >= avgNet ? 'go' : 'signal'}
                dot
                style={{ marginTop: 0 }}
              >
                {net < 0 ? 'Short' : net >= avgNet ? 'Strong' : 'Clear'}
              </PlanBadge>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={rowLabel}>Estimated income</span>
              <span style={{ fontSize: 13, color: TEXT1 }}>{fmt(m.estimated_income ?? 0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: RED }}>Business expenses</span>
              <span style={{ fontSize: 13, color: RED }}>−{fmt(m.monthly_expenses ?? m.expenses ?? 0)}</span>
            </div>
            {taxDue && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: RED }}>{taxDueLabel(m)}</span>
                <span style={{ fontSize: 13, color: RED }}>−{fmt(m.tax_reserve ?? 0)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '0.5px solid rgba(0,0,0,0.08)' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: net >= 0 ? GREEN : RED }}>Estimated net</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: net >= 0 ? GREEN : RED }}>
                {net >= 0 ? '+' : '−'}{fmt(Math.abs(net))}
              </span>
            </div>

            {isSource && holdBack != null && negMonth && (
              <p style={{ margin: '11px 0 0', fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.55 }}>
                Your best month of the quarter. Move {fmt(holdBack)} of it into reserve
                and {negativeMonthName}'s shortfall disappears.
              </p>
            )}

            {isNeg && holdBack != null && hasSurplus && (
              <>
                <button
                  onClick={() => navigate('/taxes')}
                  style={{
                    width: '100%', marginTop: 11,
                    backgroundColor: 'var(--signal)', color: 'var(--on-signal)',
                    border: 'none', borderRadius: 14, padding: 14,
                    fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Move {fmt(holdBack)}{sourceMonthName ? ` from ${sourceMonthName}` : ''} into reserve
                </button>
                {trimPerMonth != null && trimPerMonth > 0 && (
                  <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--mute)', textAlign: 'center' }}>
                    Or trim {fmt(trimPerMonth)} from {negativeMonthName ?? m.month_name} expenses
                  </p>
                )}
              </>
            )}

            {isNeg && (holdBack == null || !hasSurplus) && negativeMonthInsight && (
              <p style={{ margin: '11px 0 0', fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.55 }}>
                {negativeMonthInsight.body}
              </p>
            )}
          </div>
        );
      })}

      <p style={{
        margin: 0, marginTop: 16, paddingTop: 14,
        borderTop: '1px solid var(--line)',
        fontSize: 10.5, color: 'var(--mute)', lineHeight: 1.6,
      }}>
        Estimates based on the income pattern and expenses you entered. TruFlow is
        a financial coaching tool, not a tax advisor.
      </p>
    </>
  );
}

// Category → icon. Keyed off the fixed category list the expense forms offer;
// anything unmapped (including null) falls through to the generic receipt.
// Stroke is set via style, not a presentation attribute, so var() resolves.
function CategoryIcon({ category }) {
  const paths = {
    'Vehicle and transportation': (
      <>
        <path d="M2 11h13l3 3v3H2z" /><circle cx="6" cy="17" r="1.6" /><circle cx="14.5" cy="17" r="1.6" />
      </>
    ),
    'Tools and equipment': (
      <>
        <path d="M14 3a4 4 0 0 0-5 5L3 14l2 2 6-6a4 4 0 0 0 5-5l-2.5 2.5-2-2z" />
      </>
    ),
    'Insurance': (<><path d="M10 2l7 3v5c0 4-3 6.5-7 8-4-1.5-7-4-7-8V5z" /></>),
    'Phone and communications': (
      <>
        <rect x="6" y="2" width="8" height="16" rx="2" /><line x1="9" y1="15" x2="11" y2="15" />
      </>
    ),
    'Protective gear and uniforms': (
      <>
        <path d="M3 13a7 7 0 0 1 14 0z" /><line x1="2" y1="13" x2="18" y2="13" />
      </>
    ),
    'Training and licensing': (
      <>
        <path d="M10 3L2 7l8 4 8-4z" /><path d="M5 9v4c0 1.5 2.2 3 5 3s5-1.5 5-3V9" />
      </>
    ),
    'Marketing and advertising': (
      <>
        <path d="M4 8v4h3l6 4V4L7 8z" /><path d="M15 8a3 3 0 0 1 0 4" />
      </>
    ),
    'Software and subscriptions': (
      <>
        <rect x="2" y="3" width="16" height="11" rx="2" /><line x1="7" y1="17" x2="13" y2="17" />
      </>
    ),
    'Office and admin': (
      <>
        <path d="M2 6a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" />
      </>
    ),
    'Materials and supplies': (
      <>
        <path d="M3 6l7-3 7 3v8l-7 3-7-3z" /><path d="M3 6l7 3 7-3M10 9v8" />
      </>
    ),
    'Travel and lodging': (
      <>
        <path d="M10 18s6-5.4 6-10a6 6 0 1 0-12 0c0 4.6 6 10 6 10z" /><circle cx="10" cy="8" r="2.2" />
      </>
    ),
  };

  return (
    <svg
      width="17" height="17" viewBox="0 0 20 20" fill="none"
      style={{ stroke: 'var(--slate)' }}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
    >
      {paths[category] ?? (
        <>
          <path d="M4 2h12v16l-2.5-1.5L11 18l-1-1.5L9 18l-2.5-1.5L4 18z" />
          <line x1="7" y1="7" x2="13" y2="7" /><line x1="7" y1="11" x2="13" y2="11" />
        </>
      )}
    </svg>
  );
}

function ExpenseRow({ expense, last, navigate }) {
  const sub = expense.frequency === 'monthly'
    ? 'Monthly'
    : (expense.date ? formatEntryDate(expense.date) : 'One-time');

  return (
    <div
      onClick={() => navigate('/expenses/edit', { state: { expense } })}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        minHeight: 52, cursor: 'pointer',
        borderBottom: last ? 'none' : '1px solid var(--line)',
      }}
    >
      <span style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: 10,
        backgroundColor: 'var(--sunk)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CategoryIcon category={expense.category} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
          {expense.name}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--mute)' }}>
          {sub}
        </span>
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
        {fmt(expense.amount)}
      </span>
    </div>
  );
}

export function ExpensesCard({ expensesData, curveData, navigate, deductionInsight }) {
  const expenses = expensesData?.expenses ?? [];
  const monthlyTotal = expensesData?.monthly_total ?? 0;

  useEffect(() => {
    if (deductionInsight && expenses.length > 0) {
      posthog.capture('insight_viewed', { type: deductionInsight.type, placement: 'expenses_inline' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (expenses.length === 0) {
    return (
      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: TEXT1 }}>Monthly expenses</p>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 14, color: TEXT2 }}>
          No expenses added yet. Add your first expense.
        </p>
        <PrimaryButton label="Add expense" onClick={() => navigate('/expenses/add')} />
      </div>
    );
  }

  const recurring = expenses.filter((e) => e.frequency === 'monthly');
  const oneTime = expenses.filter((e) => e.frequency !== 'monthly');

  // Share of a typical month's income. Off the income curve already on this
  // screen; omitted entirely when there's no curve rather than fetching one.
  const avgMonthlyIncome = typeof curveData?.annual_income === 'number' && curveData.annual_income > 0
    ? curveData.annual_income / 12
    : null;
  const pctOfMonth = avgMonthlyIncome != null
    ? Math.round((monthlyTotal / avgMonthlyIncome) * 100)
    : null;

  const addButton = (
    <button onClick={() => navigate('/expenses/add')} style={{
      width: '100%', marginTop: 12, padding: '10px',
      border: `1.5px dashed rgba(0,0,0,0.18)`, borderRadius: 10,
      background: 'none', cursor: 'pointer',
      fontSize: 14, color: TEXT2,
    }}>
      + Add expense
    </button>
  );

  return (
    <>
      <div style={{
        backgroundColor: 'var(--card)', boxShadow: 'var(--sh-card)',
        borderRadius: 'var(--r-card)', padding: '22px 17px', textAlign: 'center',
      }}>
        <p style={{
          margin: 0, fontSize: 10.5, fontWeight: 700,
          letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--mute)',
        }}>
          Running each month
        </p>
        <p style={{
          margin: '6px 0 0', fontFamily: 'var(--font-serif)', fontSize: 42,
          fontWeight: 400, letterSpacing: '-.02em', color: TEXT1, lineHeight: 1.1,
        }}>
          {fmt(monthlyTotal)}
        </p>
        {pctOfMonth != null && (
          <PlanBadge tone="signal" dot>{pctOfMonth}% of a typical month</PlanBadge>
        )}
      </div>

      {recurring.length > 0 && (
        <>
          <SectionEyebrow>Recurring</SectionEyebrow>
          <div style={card()}>
            {recurring.map((exp, i) => (
              <ExpenseRow
                key={exp.id}
                expense={exp}
                last={i === recurring.length - 1}
                navigate={navigate}
              />
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0 4px' }}>
              <span style={{ fontSize: 13, color: TEXT2 }}>Monthly total</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: BLUE }}>{fmt(monthlyTotal)}</span>
            </div>
            {addButton}
          </div>
        </>
      )}

      {oneTime.length > 0 && (
        <>
          <SectionEyebrow>One-time</SectionEyebrow>
          <div style={card()}>
            {oneTime.map((exp, i) => (
              <ExpenseRow
                key={exp.id}
                expense={exp}
                last={i === oneTime.length - 1}
                navigate={navigate}
              />
            ))}
            {/* Only carries the add affordance when there is no recurring card. */}
            {recurring.length === 0 && addButton}
          </div>
        </>
      )}
    </>
  );
}

export function DeductionSavingsCard({ deductionInsight }) {
  if (!deductionInsight) return null;

  // Prefer the numeric fields the insight carries; fall back to parsing the copy
  // so the card keeps working against a backend response that predates them.
  // Every path produces a formatted string or null, so the render below is shared.
  const MONEY = /\$[\d,]+(?:\.\d+)?/;
  const data = deductionInsight.data;

  // The backend is mid-rename and one build has to serve both payloads: what is
  // deployed keys on ytd_total, while the new one leads with a monthly rate
  // because no start date is stored for a recurring expense, so a year-to-date
  // recurring total can't be substantiated. monthly_deductible selects the copy
  // rather than merging it — a monthly rate and a year-to-date total are
  // different claims and cannot share a sentence.
  const isMonthly = typeof data?.monthly_deductible === 'number';

  let heroFigure = null;
  let heroLabel = null;
  let support = null;

  if (isMonthly) {
    // A numeric 0 is an answer, not a gap: monthly_savings is 0.0 whenever the
    // effective rate is 0. It resolves to null and stops there. Falling through
    // to the old keys or the prose is what produced a $0 hero before, and it
    // would be worse here — estimated_savings is an annual figure, so anything
    // recovered that way would get "/mo" appended to a number that isn't one.
    // Absent (a partly populated object) takes the same path for the same
    // reason, degrading to the deductible hero rather than blanking the card.
    const savings = data.monthly_savings > 0 ? fmt(data.monthly_savings) : null;
    const deductible = data.monthly_deductible > 0 ? fmt(data.monthly_deductible) : null;
    // Same rule: 0 means none, so the clause is dropped rather than claiming
    // "$0 one-time this year".
    const oneTime = data.one_time_ytd > 0 ? fmt(data.one_time_ytd) : null;

    if (savings) {
      heroFigure = `${savings}/mo`;
      heroLabel = 'Estimated tax savings';
      support = [
        deductible && `${deductible}/mo in deductible expenses`,
        oneTime && `${oneTime} one-time this year`,
      ].filter(Boolean).join(' · ') || null;
    } else if (deductible) {
      // No effective rate, so the deductible rate leads and the label moves with
      // it — the same swap the year-to-date path makes when savings is 0. The
      // body already carries the annualized figure and any one-time total, so
      // restating either here would only repeat the hero.
      heroFigure = `${deductible}/mo`;
      heroLabel = 'Deducting each month';
      support = deductionInsight.body ?? null;
    } else if (oneTime) {
      // Dated one-time expenses but no recurring rate — the one payload state
      // with nothing monthly to lead on, and the only figure here that is a
      // genuine year-to-date total, so it carries no "/mo". Without this branch
      // the card would vanish for anyone who has logged only one-time expenses.
      heroFigure = oneTime;
      heroLabel = 'One-time deductions this year';
      support = deductionInsight.body ?? null;
    }
  } else {
    const deductionsFigure =
      typeof data?.ytd_total === 'number'
        ? fmt(data.ytd_total)
        : (deductionInsight.headline?.match(MONEY)?.[0] ?? null);

    // A numeric 0 is a real value meaning "no tax setup, so no effective rate" —
    // it resolves to null rather than falling through, because the body copy
    // carries no figure in that state either.
    const savingsFigure =
      typeof data?.estimated_savings === 'number'
        ? (data.estimated_savings > 0 ? fmt(data.estimated_savings) : null)
        : (deductionInsight.body?.match(MONEY)?.[0] ?? null);

    // Hero the tax-savings figure; fall back to the deductions total when no savings
    // estimate is available yet (e.g. effective rate unknown) so it never shows $0/NaN.
    heroFigure = savingsFigure ?? deductionsFigure;
    heroLabel = savingsFigure ? 'Estimated tax savings' : 'Deductions logged this year';
    support = savingsFigure
      ? (deductionsFigure ? `${deductionsFigure} in deductions logged this year` : deductionInsight.headline)
      : deductionInsight.body;
  }

  if (!heroFigure) return null;

  return (
    <div style={card({ backgroundColor: 'var(--go-soft)' })}>
      <p style={{ margin: 0, fontSize: 11, color: GREEN, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
        {heroLabel}
      </p>
      <p style={{ margin: '4px 0 2px', fontFamily: 'var(--font-serif)', fontSize: 40, fontWeight: 700, color: GREEN, lineHeight: 1.1 }}>
        {heroFigure}
      </p>
      {support && (
        <p style={{ margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.4 }}>
          {support}
        </p>
      )}
    </div>
  );
}

function LogTransferModal({ pool, onCancel, onConfirm, loading, error }) {
  const poolLabel = pool === 'tax' ? 'Tax stash' : 'Savings reserve';
  const [amount, setAmount] = useState('');
  const [entryDate, setEntryDate] = useState(todayISO());
  const [localError, setLocalError] = useState(null);

  function handleSave() {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) {
      setLocalError('Enter an amount greater than 0.');
      return;
    }
    setLocalError(null);
    onConfirm(num, entryDate);
  }

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', minWidth: 0,
    WebkitAppearance: 'none', appearance: 'none',
    padding: '10px 12px', marginTop: 6, fontSize: 15,
    border: '1px solid rgba(0,0,0,0.18)', borderRadius: 10,
    color: TEXT1,
  };
  const fieldLabel = {
    fontSize: 11, color: TEXT3, textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  return (
    <div
      onClick={loading ? undefined : onCancel}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 320, width: '100%', backgroundColor: 'var(--card)', borderRadius: 'var(--r-card)', padding: 20 }}
      >
        <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: TEXT1 }}>
          Log transfer to {poolLabel}
        </p>
        <label style={{ display: 'block' }}>
          <span style={fieldLabel}>Amount</span>
          <input
            type="number" inputMode="decimal" min="0" step="0.01"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="0" autoFocus style={inputStyle}
          />
        </label>
        <label style={{ display: 'block', marginTop: 14 }}>
          <span style={fieldLabel}>Date</span>
          <input
            type="date" value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            style={inputStyle}
          />
        </label>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1, padding: '10px 16px',
              backgroundColor: 'var(--paper)', color: TEXT2,
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 500,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              flex: 1, padding: '10px 16px',
              backgroundColor: BLUE, color: 'var(--on-signal)',
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 500,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Logging...' : 'Log transfer'}
          </button>
        </div>
        {(localError || error) && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: RED }}>
            {localError || error}
          </p>
        )}
      </div>
    </div>
  );
}

// Build the plan objects the set-aside surfaces render from. Thin pass-throughs
// to the pure planHelpers — Home and Money call these with identical inputs, so
// the numbers always match. Tax math / weight→dollar conversion live in the
// helpers, never in a component.
function buildTaxPlan(taxData) {
  if (!taxData) return null;
  return taxReservePlan({
    quarterlyReserve: taxData.recommended_reserve,
    dueDate: taxData.next_due_date ?? taxData.due_date,
    today: new Date(),
  });
}

function buildSavingsPlan(curveData, expensesData) {
  if (!curveData || !expensesData) return null;
  const dollars = curveToMonthlyDollars({
    monthWeights: curveData.month_weights ?? curveData.monthly_weights,
    annualIncome: curveData.annual_income,
  });
  if (!dollars) return null; // can't build a trustworthy dollar curve → hide savings
  return slowSeasonPlan({
    incomeCurve: dollars,
    monthlyExpenses: expensesData.monthly_total,
    today: new Date(),
  });
}

// 'plan' → render the plan-led savings section; 'hide' → render nothing. The two
// un-designed edge states (always_underwater, curve_looks_like_weights) and the
// clean no_slow_season / conversion-failure cases all hide here; the edge states
// are console.warn'd by the caller for dev visibility (copy lands in a follow-up).
function savingsSectionMode(plan) {
  if (!plan) return 'hide';
  if (plan.error) return 'hide';
  if (plan.status === 'no_slow_season') return 'hide';
  if (plan.status === 'always_underwater') return 'hide';
  return 'plan'; // on_track | behind
}

const BADGE_TONES = {
  stop:   { bg: RED_BG, fg: RED },
  signal: { bg: BLUE_LIGHT, fg: BLUE },
  go:     { bg: 'var(--go-soft)', fg: GREEN },
};

// Pill chip. `alert` is the original two-state API (kept so existing callers
// render unchanged); `tone` opts into the three-tone palette, `dot` prefixes a
// filled circle in the same colour as the label.
function PlanBadge({ alert, tone, dot, style, children }) {
  const palette = BADGE_TONES[tone ?? (alert ? 'stop' : 'signal')];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
      backgroundColor: palette.bg,
      color: palette.fg,
      fontSize: 12, fontWeight: 600, padding: '3px 10px',
      borderRadius: 20,
      ...style,
    }}>
      {dot && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: palette.fg, flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  );
}

// Elapsed-fraction pace against a fixed IRS period. Shared by HeroTaxSummary
// and the tax pool card so the two can never disagree; returns null when the
// due date doesn't map to a quarter, and callers fall back to fully-funded.
function quarterPaceTarget(dueDateStr, target, today) {
  if (typeof target !== 'number' || target <= 0) return null;
  const quarter = dueDateToQuarter(dueDateStr);
  const dueAt = dueDateStr ? new Date(dueDateStr + 'T00:00:00') : null;
  if (!quarter || !dueAt || isNaN(dueAt.getTime())) return null;
  const startMonth = QUARTER_START_MONTH[quarter.quarter];
  if (startMonth == null) return null;
  const periodStart = new Date(quarter.tax_year, startMonth, 1);
  const span = dueAt - periodStart;
  if (span <= 0) return null;
  const elapsed = Math.min(Math.max((today - periodStart) / span, 0), 1);
  return target * elapsed;
}

// One set-aside pool as its own top-level card. Leads with the BALANCE — what
// the user has actually done — against the target in the sub-line, rather than
// leading with the number they owe.
function PlanPoolSection({ pool, label, plan, data, footnote, onLogTransfer, onDeleteEntry, onPayNow }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const balance = data?.balance ?? 0;
  const recentEntries = data?.entries ?? [];
  const target = plan.target;
  const hasTarget = target != null && target > 0;
  const pct = hasTarget ? Math.min(balance / target, 1) : 0;
  const toGo = hasTarget ? Math.max(target - balance, 0) : 0;
  const months = plan.monthsRemaining;
  const funded = hasTarget && balance >= target;
  const isTax = pool === 'tax';

  const todayAt = new Date();
  todayAt.setHours(0, 0, 0, 0);
  const dueAt = isTax && plan.deadline ? new Date(plan.deadline + 'T00:00:00') : null;
  const dueValid = dueAt !== null && !isNaN(dueAt.getTime());
  const daysToDue = dueValid ? Math.round((dueAt - todayAt) / 86400000) : null;
  const quarterInfo = isTax ? dueDateToQuarter(plan.deadline) : null;

  // Behind only applies where there's a deadline to be behind against.
  const behind = isTax && hasTarget && pct < 0.25 && daysToDue !== null && daysToDue < 30;
  // Savings has no period start in the plan, so no pace target is derivable —
  // that pool only ever earns the fully-set chip.
  const paceTarget = isTax ? quarterPaceTarget(plan.deadline, target, todayAt) : null;

  let chip = null;
  if (hasTarget && balance > 0) {
    if (funded) chip = { tone: 'go', label: 'Fully set' };
    else if (behind) chip = { tone: 'stop', label: 'Behind' };
    else if (paceTarget !== null && balance >= paceTarget) chip = { tone: 'go', label: 'On track' };
  }

  const subLine = isTax
    ? `of ${fmt(target)} owed for ${quarterInfo ? `Q${quarterInfo.quarter} ${quarterInfo.tax_year}` : 'this quarter'}`
    : `of ${fmt(target)} for the slow season`;

  const perMonth = funded
    ? fmt(0)
    : (months != null && months > 0 ? fmt(toGo / months) : null);

  // Cells are collected then filtered, so a missing value drops its cell and the
  // strip renders two-up rather than showing a blank.
  const slowSeasonLabel = !isTax && plan.slowSeasonStart != null
    ? monthIndexToLabel(plan.slowSeasonStart)
    : null;
  const statCells = (isTax
    ? [
      { key: 'days', value: daysToDue !== null ? String(Math.max(daysToDue, 0)) : null, label: 'Days left', alert: behind },
      { key: 'per', value: perMonth, label: 'Per month' },
      { key: 'due', value: dueValid ? dueAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null, label: 'Due' },
    ]
    : [
      { key: 'months', value: months != null ? String(months) : null, label: 'Months' },
      { key: 'per', value: perMonth, label: 'Per month' },
      { key: 'slow', value: slowSeasonLabel ? slowSeasonLabel.slice(0, 3) : null, label: 'Slow season' },
    ]
  ).filter((c) => c.value != null);

  function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) posthog.capture('savings_history_opened', { pool });
  }

  const trackColor = behind ? RED : funded ? GREEN : BLUE;
  const primaryButton = {
    width: '100%', backgroundColor: 'var(--signal)', color: 'var(--on-signal)',
    border: 'none', borderRadius: 14, padding: 14,
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
  };
  const quietButton = {
    background: 'none', border: 'none', padding: 0,
    color: 'var(--slate)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  };

  const logButton = (
    <button onClick={onLogTransfer} style={primaryButton}>
      {balance === 0 ? 'Log your first transfer' : 'Log a transfer'}
    </button>
  );
  const historyButton = recentEntries.length > 0 ? (
    <button onClick={toggleHistory} style={quietButton}>
      {historyOpen ? 'Hide history' : 'History'}
    </button>
  ) : null;

  return (
    <div style={{
      backgroundColor: 'var(--card)', boxShadow: 'var(--sh-card)',
      borderRadius: 'var(--r-card)', padding: 18, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.015em', color: TEXT1 }}>{label}</span>
        {chip && <PlanBadge tone={chip.tone} dot style={{ marginTop: 0 }}>{chip.label}</PlanBadge>}
      </div>

      <p style={{
        margin: '10px 0 0', fontFamily: 'var(--font-serif)', fontSize: 44,
        fontWeight: 400, letterSpacing: '-.02em', lineHeight: 0.95,
        // A large black zero reads as a failure; muted reads as "not started".
        color: balance === 0 ? 'var(--mute)' : TEXT1,
      }}>
        {fmt(balance)}
      </p>
      {hasTarget && (
        <p style={{ margin: '7px 0 0', fontSize: 12.5, color: 'var(--slate)' }}>
          {subLine}
        </p>
      )}

      {hasTarget && (
        <>
          <div style={{ height: 7, borderRadius: 4, backgroundColor: SURFACE, overflow: 'hidden', marginTop: 14 }}>
            <div style={{ height: '100%', width: `${pct * 100}%`, backgroundColor: trackColor }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--slate)' }}>
              {balance === 0 ? 'Nothing logged yet' : `${Math.round(pct * 100)}% there`}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: funded ? GREEN : behind ? RED : TEXT1,
            }}>
              {funded ? 'Nothing left to set aside' : `${fmt(toGo)} to go`}
            </span>
          </div>
        </>
      )}

      {statCells.length > 0 && (
        <div style={{
          display: 'flex', backgroundColor: 'var(--sunk)',
          borderRadius: 12, padding: '12px 4px', marginTop: 14,
        }}>
          {statCells.map((cell, i) => (
            <div key={cell.key} style={{
              flex: 1, textAlign: 'center',
              borderLeft: i > 0 ? '1px solid var(--line)' : 'none',
            }}>
              <p style={{
                margin: 0, fontSize: 16, fontWeight: 600,
                letterSpacing: '-.025em', color: cell.alert ? RED : TEXT1,
              }}>
                {cell.value}
              </p>
              <p style={{
                margin: '5px 0 0', fontSize: 9.5, fontWeight: 600,
                letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--mute)',
              }}>
                {cell.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {footnote}

      {funded && isTax ? (
        <>
          {/* Secondary: the reserve card higher on this screen already carries
              the solid pay button, so this one steps down rather than competing. */}
          <button
            onClick={onPayNow}
            style={{
              ...primaryButton, marginTop: 14,
              backgroundColor: 'transparent',
              border: '1px solid var(--signal)',
              color: 'var(--signal)',
            }}
          >
            Pay now — IRS Direct Pay
          </button>
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.55 }}>
            The money's there. Pay it whenever you like — paying early costs nothing
            and ends the countdown.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button onClick={onLogTransfer} style={quietButton}>Log a transfer</button>
            {historyButton}
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
          <span style={{ flex: 1 }}>{logButton}</span>
          {historyButton}
        </div>
      )}

      {historyOpen && recentEntries.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {recentEntries.map((e) => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '0.5px solid rgba(0,0,0,0.06)' }}>
              <span style={{ fontSize: 12, color: TEXT2 }}>{formatEntryDate(e.entry_date)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: e.amount < 0 ? RED : TEXT1 }}>
                  {e.amount < 0 ? '−' : '+'}{fmt(Math.abs(e.amount))}
                </span>
                {e.source === 'manual' && (
                  <button
                    onClick={() => onDeleteEntry(e.id, pool)}
                    aria-label="Delete entry"
                    style={{ background: 'none', border: 'none', color: TEXT3, fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '2px 4px' }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SavingsCard({ savingsData, taxData, curveData, expensesData, containerRef, modalPool, onOpenModal, onCloseModal, onChanged, navigate }) {
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const taxPlan = buildTaxPlan(taxData);
  const savingsPlan = buildSavingsPlan(curveData, expensesData);
  const showSavings = savingsSectionMode(savingsPlan) === 'plan';

  // Dev-visibility for the two un-designed edge states (user copy is a follow-up).
  useEffect(() => {
    if (savingsPlan?.error) {
      console.warn('[SavingsCard] savings plan unavailable:', savingsPlan.error);
    } else if (savingsPlan?.status === 'always_underwater') {
      console.warn('[SavingsCard] savings status:', savingsPlan.status, '— expenses exceed income year-round');
    }
  }, [savingsPlan?.error, savingsPlan?.status]);

  if (!savingsData) return null;
  // Nothing to show if there's no tax plan and the savings section is hidden.
  if (!taxPlan && !showSavings) return null;

  async function handleConfirmTransfer(amount, entryDate) {
    setModalLoading(true);
    setModalError(null);
    try {
      await createSavingsEntry({ amount, pool: modalPool, entryDate });
      posthog.capture('savings_transfer_logged', { pool: modalPool });
      onCloseModal();
      await onChanged();
    } catch {
      setModalError("Couldn't log that transfer. Please try again.");
    }
    setModalLoading(false);
  }

  async function handleDeleteEntry(entryId, pool) {
    try {
      await deleteSavingsEntry(entryId);
      posthog.capture('savings_entry_deleted', { pool });
      await onChanged();
    } catch {}
  }

  function handleCloseModal() {
    setModalError(null);
    onCloseModal();
  }

  return (
    <div ref={containerRef}>
      <SectionEyebrow>Set aside</SectionEyebrow>

      {taxPlan && (
        <PlanPoolSection
          pool="tax" label="Tax stash" plan={taxPlan} data={savingsData.tax}
          onLogTransfer={() => onOpenModal('tax')} onDeleteEntry={handleDeleteEntry}
          onPayNow={() => { posthog.capture('irs_payment_guide_opened'); navigate?.('/irs-payment'); }}
        />
      )}

      {showSavings && (
        <PlanPoolSection
          pool="savings" label="Savings reserve" plan={savingsPlan} data={savingsData.savings}
          footnote={savingsPlan.status === 'behind' && savingsPlan.finishMonth != null ? (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.55 }}>
              At this pace you'll have it set by {monthIndexToLabel(savingsPlan.finishMonth)} — a little after the slow season starts.
            </p>
          ) : null}
          onLogTransfer={() => onOpenModal('savings')} onDeleteEntry={handleDeleteEntry}
        />
      )}

      {modalPool && (
        <LogTransferModal
          pool={modalPool}
          onCancel={handleCloseModal}
          onConfirm={handleConfirmTransfer}
          loading={modalLoading}
          error={modalError}
        />
      )}
    </div>
  );
}

// ── Home (Dashboard) summary cards ───────────────────────────────────────────
// Light, glanceable summaries that tap through to the full detail screens
// (/taxes, /cashflow, /money). The heavy cards above are exported and rendered
// on those screens now; these only surface the headline numbers on Home.

function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ stroke: TEXT3 }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 2 10 7 5 12" />
    </svg>
  );
}

function TappableCard({ onClick, label, children }) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={card({ cursor: 'pointer' })}
    >
      {children}
    </div>
  );
}

// Hero of the Home screen — the headline quarterly reserve number. Taps to
// /taxes for the full breakdown + pay flow. Preserves the incomplete-setup
// prompt (matches the full TaxReserveCard's empty state).
function HeroTaxSummary({ taxData, savingsData, navigate }) {
  if (!taxData) {
    return (
      <div style={card()}>
        <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: TEXT1 }}>Quarterly tax reserve</p>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: TEXT2 }}>Complete your tax setup to see your reserve.</p>
        <PrimaryButton label="Set up taxes" onClick={() => navigate('/onboarding/tax')} />
      </div>
    );
  }

  const { recommended_reserve, next_due_date, due_date } = taxData;
  const resolvedDueDate = next_due_date ?? due_date;
  const deadlineBadge = formatDeadlineBadge(resolvedDueDate);
  const badgeAlert = deadlineBadge?.urgency === 'critical' || deadlineBadge?.urgency === 'overdue';

  // Set-aside progress. Target comes from buildTaxPlan — the same helper the
  // Money tab's PlanPoolSection reads — so the two screens can't drift apart;
  // balance is the same savingsData.tax slice that screen uses. No extra fetch:
  // /savings/summary is already in the shared hook's batch.
  const taxPlan = buildTaxPlan(taxData);
  const target = taxPlan?.target ?? null;
  const balance = savingsData?.tax?.balance ?? 0;
  const showProgress = target != null && target > 0;
  const pct = showProgress ? Math.min(balance / target, 1) : 0;
  const toGo = showProgress ? Math.max(target - balance, 0) : 0;

  // Behind = under a quarter of the way there with the deadline inside 30 days.
  // Parsed at local midnight to match formatDeadlineBadge and dateHelpers.
  const dueAt = resolvedDueDate ? new Date(resolvedDueDate + 'T00:00:00') : null;
  const todayAt = new Date();
  todayAt.setHours(0, 0, 0, 0);
  const daysToDue = dueAt && !isNaN(dueAt.getTime())
    ? Math.round((dueAt - todayAt) / 86400000)
    : null;
  const behind = showProgress && pct < 0.25 && daysToDue !== null && daysToDue < 30;

  // Pace. PlanPoolSection shows plan.monthlyTarget, which taxReservePlan derives
  // from the FULL target — here it has to describe what's still owed, so it's
  // recomputed from toGo over the same plan.monthsRemaining. Inside a single
  // contribution month a per-month rate says nothing, so name the date instead.
  const paceDeadline = dueAt && !isNaN(dueAt.getTime())
    ? dueAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;
  const paceMonths = taxPlan?.monthsRemaining ?? null;
  let paceLine = null;
  if (showProgress && toGo > 0 && paceMonths !== null) {
    paceLine = paceMonths > 1
      ? `about ${fmt(toGo / paceMonths)}/mo over the next ${paceMonths} months`
      : (paceDeadline ? `about ${fmt(toGo)} before ${paceDeadline}` : null);
  }

  // Three-up strip. Built as a list so a missing piece drops its cell rather
  // than rendering a blank one — no extra request is made to fill a gap.
  const statCells = [];
  if (daysToDue !== null) {
    statCells.push({ key: 'days', value: String(Math.max(daysToDue, 0)), label: 'Days left', alert: behind });
  }
  statCells.push({ key: 'togo', value: fmt(toGo), label: 'To go', alert: false });
  if (typeof taxData.effective_rate === 'number') {
    statCells.push({ key: 'rate', value: `${(taxData.effective_rate * 100).toFixed(1)}%`, label: 'Your rate', alert: false });
  }
  const showStats = showProgress && statCells.length >= 2;

  return (
    <TappableCard onClick={() => navigate('/taxes')} label="View tax breakdown and pay">
      <p style={{ margin: '0 0 2px', fontSize: 11, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quarterly tax reserve</p>
      <p style={{ margin: '4px 0 2px', fontFamily: 'var(--font-serif)', fontSize: 40, fontWeight: 700, color: TEXT1, lineHeight: 1.1 }}>
        {fmt(recommended_reserve)}
      </p>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: TEXT2 }}>Recommended this quarter</p>
      {deadlineBadge && (
        <span style={{
          display: 'inline-block', marginBottom: 14,
          backgroundColor: badgeAlert ? RED_BG : BLUE_LIGHT,
          color: badgeAlert ? RED : BLUE,
          fontSize: 12, fontWeight: 600, padding: '3px 10px',
          borderRadius: 20,
        }}>
          {deadlineBadge.label}
        </span>
      )}
      {showProgress && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ height: 6, borderRadius: 3, backgroundColor: SURFACE, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct * 100}%`, backgroundColor: behind ? RED : BLUE }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: TEXT2 }}>
              {balance > 0 ? `${fmt(balance)} set aside` : 'Nothing set aside yet'}
            </span>
            <span style={{ fontSize: 12, color: behind ? RED : TEXT2 }}>
              {fmt(toGo)} to go
            </span>
          </div>
          {paceLine && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: behind ? RED : TEXT3 }}>
              {paceLine}
            </p>
          )}
        </div>
      )}
      {showStats && (
        <div style={{
          display: 'flex', backgroundColor: 'var(--sunk)',
          borderRadius: 12, padding: '12px 4px', marginTop: 14,
        }}>
          {statCells.map((cell, i) => (
            <div key={cell.key} style={{
              flex: 1, textAlign: 'center',
              borderLeft: i > 0 ? '1px solid var(--line)' : 'none',
            }}>
              <p style={{
                margin: 0, fontSize: 16, fontWeight: 600,
                letterSpacing: '-.025em', color: cell.alert ? RED : TEXT1,
              }}>
                {cell.value}
              </p>
              <p style={{
                margin: '5px 0 0', fontSize: 9.5, fontWeight: 600,
                letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--mute)',
              }}>
                {cell.label}
              </p>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '0.5px solid rgba(0,0,0,0.07)', paddingTop: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: BLUE }}>View breakdown &amp; pay</span>
        <Chevron />
      </div>
    </TappableCard>
  );
}

export function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: `3px solid ${BLUE_LIGHT}`,
        borderTopColor: BLUE,
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rawName = user?.first_name ?? user?.firstName ?? 'there';
  const firstName = rawName.charAt(0).toUpperCase() + rawName.slice(1);

  // Home now reads from the shared data hook (same fetch the detail screens use).
  // It only needs the headline slices for the summaries — the paid-quarters /
  // savings-modal / refetch wiring stays in the hook for the detail screens.
  const {
    loading,
    taxData,
    taxSetupData,
    savingsData,
    insightsData,
    cashflowData,
    negativeMonthInsight,
  } = useTruflowData();

  return (
    <div style={{ backgroundColor: SURFACE, minHeight: '100vh' }}>
      <StickyHeader navigate={navigate} />
      {/* Bottom padding clears the fixed BottomTabBar (height + breathing room + safe-area inset). */}
      <div style={{
        maxWidth: 480, margin: '0 auto', padding: '0 16px',
        paddingBottom: `calc(${TAB_BAR_HEIGHT + 12}px + max(env(safe-area-inset-bottom), 8px))`,
      }}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Greeting firstName={firstName} />
            <InsightCard
              insights={insightsData}
              cashflowData={cashflowData}
              negativeMonthInsight={negativeMonthInsight}
              navigate={navigate}
              onAddExpense={() => navigate('/expenses/add')}
              onStartBanking={() => navigate('/taxes')}
            />
            <SectionEyebrow>This quarter</SectionEyebrow>
            <HeroTaxSummary taxData={taxData} savingsData={savingsData} navigate={navigate} />
            <SectionEyebrow>Next 90 days</SectionEyebrow>
            <CashflowSummaryCard
              insights={insightsData}
              cashflowData={cashflowData}
              negativeMonthInsight={negativeMonthInsight}
              navigate={navigate}
            />
            <OverviewCard taxData={taxData} taxSetupData={taxSetupData} navigate={navigate} />
            <p style={{ textAlign: 'center', fontSize: 10, color: TEXT3, margin: '20px 0 0', lineHeight: 1.6 }}>
              Estimates based on information you entered. TruFlow is not a tax advisor. Consult a CPA for personalized advice.
            </p>
          </>
        )}
      </div>
      <BottomTabBar />
    </div>
  );
}
